import MessageBroker from '@Services/MessageBroker';
import RequestUrls from '@Constants/RequestUrlsConstants';
import { citations } from '@Constants';
import searchStorage from '@Services/SearchStorage';
import { SearchTypes } from '@Constants/CommonConstants';
import Breadcrumb from '@Services/Breadcrumb';
import * as ResultsDataManage from '@Services/ResultsDataManage';
import { withGlobalSpinnerActionEnhancer } from '@Utils/Redux/withGlobalSpinnerActionEnhancer';
import { newToastMessage } from '@Containers/ToastMessages/ToastMessagesActions';
import { trackCopyToClipboard } from '@Containers/Summary/analytics';
import { replaceRefsInSummaryWithIndexes } from '@Containers/Summary/summaryRefsUtil';
import formatReferencesCopyToClipboard from '@Containers/Summary/formatReferencesCopyToClipboardUtil';
import {
  SUMMARY_STATUS,
  SUMMARY_POLLING_INTERVAL,
  SUMMARY_POLLING_STATUS,
  SET_SUMMARY_STATUS,
  SET_SUMMARY_RESPONSE,
  SET_SUMMARY_ERROR,
  RESET_SUMMARY_STATE,
} from './constants';

let summaryStatusPollTimeoutId = null;

const clearSummaryStatusPolling = () => {
  if (summaryStatusPollTimeoutId) {
    clearTimeout(summaryStatusPollTimeoutId);
    summaryStatusPollTimeoutId = null;
  }
};

export function fetchSummaryStatus({ resultName, semanticResultName, databaseId, query }) {
  return async (dispatch) => {
    try {
      const response = await MessageBroker.send(RequestUrls.aiSearchSummaryStatus, {
        resultName,
      });

      const pollingStatus = response?.status;

      if (pollingStatus === SUMMARY_POLLING_STATUS.PENDING) {
        clearSummaryStatusPolling();
        summaryStatusPollTimeoutId = setTimeout(() => {
          dispatch(fetchSummaryStatus({ resultName, semanticResultName, databaseId, query }));
        }, SUMMARY_POLLING_INTERVAL);
        return;
      }

      if (pollingStatus === SUMMARY_POLLING_STATUS.COMPLETED) {
        clearSummaryStatusPolling();
        // eslint-disable-next-line no-use-before-define
        dispatch(fetchSummary({ resultName, semanticResultName, databaseId, query, skipPolling: true }));
        return;
      }

      if (pollingStatus === SUMMARY_POLLING_STATUS.ERROR) {
        clearSummaryStatusPolling();
        dispatch({ type: SET_SUMMARY_ERROR, payload: { summaryError: response } });
        dispatch({ type: SET_SUMMARY_STATUS, payload: { summaryStatus: SUMMARY_STATUS.ERROR } });
      }
    } catch (error) {
      clearSummaryStatusPolling();
      dispatch({ type: SET_SUMMARY_ERROR, payload: { summaryError: error } });
      dispatch({ type: SET_SUMMARY_STATUS, payload: { summaryStatus: SUMMARY_STATUS.ERROR } });
      Logger.error('Error while fetching summary status:', error);
    }
  };
}

export function fetchSummary({ resultName, semanticResultName, databaseId, query, skipPolling = false }) {
  return async (dispatch) => {
    try {
      clearSummaryStatusPolling();
      dispatch({ type: SET_SUMMARY_STATUS, payload: { summaryStatus: SUMMARY_STATUS.LOADING } });

      const { status, data: summaryResponse } = await MessageBroker.send(RequestUrls.aiSearchGetSummary, {
        resultName,
        semanticResultName,
        databaseId,
        query,
      });

      if (status === 202) {
        if (!skipPolling) {
          dispatch(fetchSummaryStatus({ resultName, semanticResultName, databaseId, query }));
        }
        return;
      }

      dispatch({ type: SET_SUMMARY_RESPONSE, payload: { summaryResponse } });
      dispatch({ type: SET_SUMMARY_STATUS, payload: { summaryStatus: SUMMARY_STATUS.SUCCESS } });
    } catch (summaryError) {
      clearSummaryStatusPolling();
      dispatch({ type: SET_SUMMARY_ERROR, payload: { summaryError } });
      dispatch({ type: SET_SUMMARY_STATUS, payload: { summaryStatus: SUMMARY_STATUS.ERROR } });
      Logger.error('Error while fetching summary:', summaryError);
    }
  };
}

export const limitToReferencedDocuments = withGlobalSpinnerActionEnhancer(({ cnrList, databaseId, sort, order }) => async () => {
  try {
    const response = await MessageBroker.send(RequestUrls.aiSearchLimitToReferencedDocuments, {
      databaseId,
      context: citations,
      cnrList,
    });

    const searchId = await searchStorage.setNew({
      searchType: SearchTypes.SUMMARY_REFERENCED_DOCUMENTS,
    });

    const { resultSize } = response;

    const searchParams = {
      ...response,
      sort,
      order,
      searchId,
      resetBreadcrumbs: false,
      hideSummary: true,
    };

    await Breadcrumb.createBreadcrumb(searchParams, resultSize);
    ResultsDataManage.redirectToResults(searchParams);
  } catch (error) {
    Logger.error('Error while limiting to referenced documents:', error);
  }
});

export const copySummaryToClipboard =
  ({ summary, referencedCitations }) =>
  async () => {
    try {
      trackCopyToClipboard();

      const summarySection = replaceRefsInSummaryWithIndexes(summary, referencedCitations);
      const referencesSection = formatReferencesCopyToClipboard(summary, referencedCitations);

      // eslint-disable-next-line custom/no-unsafe-deep-access
      await navigator.clipboard.writeText(`${summarySection}${referencesSection}`);

      newToastMessage({
        content: 'Summary copied successfully to clipboard.',
      });
    } catch (e) {
      Logger.error('Error while copying summary:', e);
    }
  };

export const resetSummaryState = () => {
  clearSummaryStatusPolling();
  return { type: RESET_SUMMARY_STATE };
};
